import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChooseQuoteContacts } from '~/api/sellerContacts';
import { SellerContactsModal } from '~/components/common/SellerContactsModal';
import { sellerLabel } from '~/utils';
import { buyerQuoteActions } from './quoteNegotiation';
import type { Quote } from './splitAwardGridTypes';

type ContactsQuote = Pick<
  Quote,
  | 'id'
  | 'status'
  | 'requestForQuote'
  | 'sellingOrganization'
  | 'primaryContact'
  | 'recipients'
  | 'lockedRecipientIds'
>;

type QuoteContactsButtonProps = Readonly<{
  quote: ContactsQuote;
}>;

type QuoteContactsModalProps = Readonly<{
  quote: ContactsQuote;
  onClose: () => void;
}>;

function QuoteContactsModal({ quote, onClose }: QuoteContactsModalProps) {
  const { t } = useTranslation(['rfq-view', 'general']);
  const { mutateAsync: chooseQuoteContacts, isPending: isSaving } =
    useChooseQuoteContacts();

  return (
    <SellerContactsModal
      owner={quote}
      subtitle={t('rfq_view.contacts_modal.subtitle')}
      lockedLabel={t('general:select_contacts_modal.acted_on_quote')}
      save={(recipientUserIds, primaryContactUserId) =>
        chooseQuoteContacts({
          quoteId: quote.id,
          primaryContactUserId,
          recipientUserIds,
        })
      }
      isSaving={isSaving}
      onClose={onClose}
    />
  );
}

export function QuoteContactsButton({ quote }: QuoteContactsButtonProps) {
  const { t } = useTranslation(['rfq-view', 'general']);
  const [isOpen, setIsOpen] = useState(false);
  const sellerName = sellerLabel(quote.sellingOrganization);

  return (
    <>
      <button
        type="button"
        className="text-left underline decoration-dotted underline-offset-2 hover:text-prim cursor-pointer disabled:no-underline disabled:cursor-default disabled:hover:text-inherit"
        aria-label={t('rfq_view.choose_contacts_aria_label', { sellerName })}
        onClick={() => setIsOpen(true)}
        disabled={!buyerQuoteActions(quote).chooseContacts}
        data-testid={`quote-contacts-button-${quote.id}`}
      >
        {sellerName}
      </button>
      {isOpen && (
        <QuoteContactsModal quote={quote} onClose={() => setIsOpen(false)} />
      )}
    </>
  );
}
